import React, { useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useState, useEffect, usePrintContext, Hooks, Form, Field, Name, Title, Info, Code, Actions, Toast,  API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/PrintImports';
import { Exam } from "models/ExamModel";
import { Client } from 'models/ClientModel';
import { CommonDataColumn } from 'models/BaseModel';
import { Doctor } from 'models/DoctorModel';
import { User } from 'models/UserModel';
import { Location } from 'models/LocationModel';
import { borderTopStyle } from 'html2canvas/dist/types/css/property-descriptors/border-style';


const ExamPrint2: React.FC = () => {
  const [formId, formName] = [Code.Form.Id.EXAM, Code.Form.Name.EXAM];
  const {keyId, loading, setLoading, showMenu, setShowMenu, message, setMessage, error, setError, menuPosition, setMenuPosition, contextMenuRef } = usePrintContext();
  const [formData, setFormData] = useState<Exam>({});
  const [clientData, setClientData] = useState<Client | null>(null);
  const [doctorData, setDoctorData] = useState<Doctor | null>(null);
  const [userData, setUserData] = useState<User | null>(null);
  const [locationData, setLocationData] = useState<Location | null>(null);
  const [examData, setExamData] = useState<CommonDataColumn | null>(null);

  const [locationEERNumber, setLocationEERNumber] = useState<string>('');

  const [searchParams] = useSearchParams();
  const dataId = searchParams.get('data') || '1';


  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  const printRef = useRef<HTMLDivElement>(null);
  Hooks.Privilege.HasPrint(Privilege);

  useEffect(() => {
     API.Data.GetByKeyId<Exam>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
      const formData = await Form.Map.Data(data);
      setFormData((prevData) => ({
        ...prevData,
        ...formData,
      }));

      const fieldName = `${Name.Exam.Data}${dataId}`;
      const tempExamData = (data as any)[fieldName];

      setExamData(await Files.List.extractCommonData(tempExamData));
      setClientData(await API.Data.GetClientCity(data.client_id));
      setDoctorData(await API.Data.GetDoctor(data.doctor_id));
      setUserData(await API.Data.GetUser(data.user_id));
      setLocationData(await API.Data.GetLocation(data.location_id));
    }})
    setLoading(false);
  }, []); 

  useEffect(() => {
    if(locationData){
      const locData = locationData.location_data; //sample value in locData = {"walmarteer": "3012"}

      if(locData){
        const parsed = JSON.parse(locData) as { walmarteer?: string };
        const walmarteer = parsed?.walmarteer ?? '';
        setLocationEERNumber(walmarteer);
      }
    }
  }, [locationData]); 

  return (
    <Control.Print.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      {!loading && (<>
        <Messages.Toast Messages={message} Error={error} />
        <Button.Print keyId={keyId} formName={"Eye Examination Record"} Privilege={Privilege}  clientData={clientData} fileName='exam' showModify={false} printRef={printRef} setMessage={setMessage} setError={setError} />
        <Control.Print.Data ref={printRef}>
          <div className="lab-head">
            <div className="flbox">
              <div className="fleft page-col80">
                <div className='fwb'>EYE EXAMINATION RECORD</div>
                <div className="flbox pr3 pt2">
                  <div className="fleft page-col15 fs10">LOCATION#:</div>
                  <div className="fleft page-col25 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{locationEERNumber}</div>
                  <div className="fleft page-col10 fs10 tright">DR:</div>
                  <div className="fleft page-col50 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{`${doctorData?.doctor_first_name || ''} ${doctorData?.doctor_middle_name || ''} ${doctorData?.doctor_last_name || ''}`.replace('Dr.','')}</div>
                </div>
              </div>
              <div className="fleft page-col20 lab-all pa1">
                  <div className="flbox">
                    <div className="fleft page-col50 fs11 lab-lh28">DIABETES</div>
                    <div className="fleft page-col50 fs11 tcenter">
                      <div className={`lab-lh14 ${examData && examData?.D63 && examData.D63.toLowerCase() != 'none' && examData.D63.toLowerCase() != '' ? 'lab-round' : ''}`}>YES</div>
                      <div className={`lab-lh14 ${examData && examData?.D63 && examData.D63.toLowerCase() == 'none' ? 'lab-round' : ''}`}>NO</div>
                    </div>
                  </div>
              </div>
            </div>
            <div className="flbox pt2">
              <div className="fleft page-col12 fs10">LAST NAME:</div>
              <div className="fleft page-col38 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_last_name || ''}</div>
              <div className="fleft page-col15 fs10 tright">FIRST NAME:</div>
              <div className="fleft page-col35 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_first_name || ''}</div>
            </div>
            <div className="flbox pt2">
              <div className="fleft page-col10 fs10">ADDRESS:</div>
              <div className="fleft page-col90 fs12 fwb lab-bottom pl2" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_address || ''}</div>
            </div>
            <div className="flbox pt2">
              <div className="fleft page-col5 fs10">CITY:</div>
              <div className="fleft page-col23 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.city_name || ''}</div>
              <div className="fleft page-col13 fs10 tright">PROVINCE:</div>
              <div className="fleft page-col22 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>AB</div>
              <div className="fleft page-col17 fs10 tright">POSTAL CODE:</div>
              <div className="fleft page-col20 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_postal?.toUpperCase() || ''}</div>
            </div>
            <div className="flbox pt2 mb2">
              <div className="fleft page-col12 fs10">TEL: HOME:</div>
              <div className="fleft page-col23 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{Utility.Format.Phone(clientData?.client_phone || '')}</div>
              <div className="fleft page-col6 fs10 tright">BUS:</div>
              <div className="fleft page-col23 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col8 fs10 tright">DATE:</div>
              <div className="fleft page-col28 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}>{Utility.Format.Date(formData?.exam_date!,"MM / dd / yyyy")}</div>
            </div>
            <div className="flbox">
              <div className="fleft page-col100 fs8 tright pr1">VER.</div>
            </div>
            <div className="flbox lab-all">
              <div className="fleft page-col12 fs10 pl1">D.O.B.</div>
              <div className="fleft page-col21 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{Utility.Format.Date(clientData?.client_dob!,"MM / dd / yyyy")}</div>
              <div className="fleft page-col14 fs10 tcenter lab-right lab-lh14">HEALTH<br/>CARD NO</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(0, 1) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(1, 2) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(2, 3) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(3, 4) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(4, 5) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>-</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(5, 6) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(6, 7) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(7, 8) || ''}</div>
              <div className="fleft page-col5 fs12 fwb lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{clientData?.client_health_card?.substring(8, 9) || ''}</div>
              <div className="fleft page-col5 fs12 fwb tcenter lab-lh28 lab-all" contentEditable={true} suppressContentEditableWarning={true}>M</div>
            </div>
            <div className="flbox lab-info">
              I hereby consent to the disclosure of my eye care patient records by my health care provider to Wal-Mart Canada Inc. for the purpose of it fulfilling its obligation to my health care provider with respect to the provision of secretarial and administrative support services, including without limitation, management of patient files, scheduling eye care appointments, issuing reminder notices for eye care appointments, telephone answering, fee collection (if applicable), and processing provincial health insurance fee claims (if applicable).
            </div>
            <div className="flbox pt2">
              <div className="fleft page-col20 fs10">Signature of Patient</div>
              <div className="fleft page-col80 fs12 fwb lab-bottom pl2" contentEditable={true} suppressContentEditableWarning={true}></div>
            </div>
            <div className="flbox mt1 lab-top lab-bottom">
              <div className="fleft page-col70 lab-right">
                <div className="flbox lab-bottom">
                  <div className="fleft page-col15 fs10 lab-right tcenter">Rx</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter">sph</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter">cyl</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter">axis</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter">prism</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter">F1</div>
                  <div className="fleft page-col15 fs10 tcenter">add</div>
                </div>
                <div className="flbox lab-bottom">
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28">O.D.</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R67 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R68 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R69 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R71 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col15 fs12 tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R75 || ''}</div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28">O.S.</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L67 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L68 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L69 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L71 || ''}</div>
                  <div className="fleft page-col15 fs12 lab-right tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col15 fs12 tcenter lab-lh28 fwb" contentEditable={true} suppressContentEditableWarning={true} >{examData?.L75 || ''}</div>
                </div>
              </div>
              <div className="fleft page-col15 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col15">
                <div className="fs11 tcenter">P D</div>
                <div className="fs11 tcenter lab-bottom">/</div>
                <div className="fs11 tcenter lab-bottom lab-lh20">VerteX dist</div>
                <div className="fs11 pl2 lab-lh20">C T</div>
              </div>
            </div>
            <div className="flbox pt2">
              <div className="fleft page-col25 fs10">DISPOSAL - Rx GIVEN</div>
              <div className='fleft page-col5 lab-lh14 fs11 tcenter'>Y</div>
              <div className='fleft page-col5 lab-lh14 fs11 tcenter'>N</div>
              <div className="fleft page-col15 fs10 pl4">REF TO</div>
              <div className="fleft page-col45 fs12 fwb pl2" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col5 fs8">1A-01</div>
            </div>
            <div className="flbox pt1">
              <div className="fleft page-col22 fs10">DIAGNOSIS</div>
              <div className='fleft page-col5'></div>
              <div className="fleft page-col15 fs10 lab-all tcenter">MYOPIA</div>
              <div className='fleft page-col5'></div>
              <div className="fleft page-col15 fs10 lab-all tcenter">ASTIGMATISM</div>
              <div className='fleft page-col5'></div>
              <div className="fleft page-col15 fs10 lab-all tcenter">HYPEROPIA</div>
              <div className='fleft page-col5'></div>
              <div className="fleft page-col15 fs10 lab-all tcenter">PRESBYOPIA</div>
            </div>
            <div className="flbox pt1">
              <div className="fleft page-col22 fs10">&nbsp;</div>
              <div className='fleft page-col5 fs10 tright pr1'>OTHER:</div>
              <div className="fleft page-col40 fs12 fwb lab-bottom tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
            </div>
            <div className="flbox pt1">
              <div className="fleft page-col60 fs10">&nbsp;</div>
              <div className='fleft page-col5 fs10 tright pr1'>LOCATION:</div>
              <div className="fleft page-col30 fs12 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
            </div>
            <div className="flbox">
              <div className="fleft page-col15 fs10">FAMILY MD:</div>
              <div className="fleft page-col55 fs12 fwb pl2" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col10 fs10">REFERRAL#</div>
              <div className="fleft page-col20 fs12 fwb pl2" contentEditable={true} suppressContentEditableWarning={true}></div>
            </div>
            <div className="flbox lab-top lab-bottom">
              <div className="fleft page-col14 lab-right">
                <div className="fs10 tcenter lab-lh16">UNAIDED<br/> V.A.</div>
              </div>
              <div className="fleft page-col43 lab-right">
                <div className="fs11 pl1 fs11 lab-lh18">D</div>
                <div className="flbox">
                  <div className="fleft page-col33 fs10 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}>R {examData?.R37 || '20/'}</div>
                  <div className="fleft page-col33 fs10 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}>L {examData?.L37 || '20/'}</div>
                  <div className="fleft page-col33 fs10 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}>B {examData?.B37 || '20/'}</div>
                </div>
              </div>
              <div className="fleft page-col43">
                <div className="flbox">
                  <div className="fleft page-col16 fs11 tcenter fs11 lab-lh20">N</div>
                  <div className="fleft page-col28 fs11 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col28 fs11 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col28 fs11 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col16 fs11 fs11 tcenter">R</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">M</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">L</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">M</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">B</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">M</div>
                </div>
              </div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col14 lab-right">
                <div className="fs10 tcenter lab-lh16">AIDED<br/> V.A.</div>
              </div>
              <div className="fleft page-col43 lab-right">
                <div className="fs11 pl1 fs11 lab-lh18">D</div>
                <div className="flbox">
                  <div className="fleft page-col33 fs10 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}>R {examData?.R40 || '20/'}</div>
                  <div className="fleft page-col33 fs10 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}>L {examData?.L40 || '20/'}</div>
                  <div className="fleft page-col33 fs10 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}>B {examData?.B40 || '20/'}</div>
                </div>
              </div>
              <div className="fleft page-col43">
                <div className="flbox">
                  <div className="fleft page-col16 fs11 tcenter fs11 lab-lh20">N</div>
                  <div className="fleft page-col28 fs11 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col28 fs11 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col28 fs11 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col16 fs11 fs11 tcenter">R</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">M</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">L</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">M</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">B</div>
                  <div className="fleft page-col16 fs11 fs11 tcenter">M</div>
                </div>
              </div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col14 lab-right">
                <div className="fs9 tcenter pb3">Present</div>
                <div className="fs9 tcenter pt3">Rx</div>
              </div>
              <div className="fleft page-col43">
                <div className="flbox">
                  <div className="fleft page-col25 fs9 lab-right tcenter">SPH</div>
                  <div className="fleft page-col25 fs9 lab-right tcenter">CYL</div>
                  <div className="fleft page-col25 fs9 lab-right tcenter">AXIS</div>
                  <div className="fleft page-col25 fs9 lab-right tcenter">PRISM</div>
                </div>
                <div className="flbox lab-bottom">
                  <div className="fleft page-col25 fs10 lab-right lab-lh16 pl1" contentEditable={true} suppressContentEditableWarning={true}>Rt {examData?.R10 || ''}</div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R11 || ''}</div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R12 || ''}</div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R14 || ''}</div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col25 fs10 lab-right lab-lh28 pl1" contentEditable={true} suppressContentEditableWarning={true}>Lt {examData?.L10 || ''}</div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L11 || ''}</div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L12 || ''}</div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L14 || ''}</div>
                </div>
              </div>
              <div className="fleft page-col43">
                <div className="flbox">
                  <div className="fleft page-col25 fs9 lab-right tcenter">F1</div>
                  <div className="fleft page-col25 fs9 lab-right tcenter">ADD</div>
                  <div className="fleft page-col15 fs9 lab-right tcenter">PD</div>
                  <div className="fleft page-col35 fs9 tcenter">&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh16 lab-bottom" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh16 lab-bottom" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col15 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col35 fs10 tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col25 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col15 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col35 fs10 tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
              </div>
            </div>
            <div className="flbox">
              <div className="fleft page-col16 fs9 lab-right tcenter">AUTO<br/>REFRACTION</div>
              <div className="fleft page-col12 fs9 lab-right tcenter">CYL</div>
              <div className="fleft page-col12 fs9 lab-right tcenter">AXIS</div>
              <div className="fleft page-col12 fs9 lab-right tcenter">V.A.</div>
              <div className="fleft page-col12 fs9 lab-right tcenter">DYNAMIC<br/>SPHERE</div>
              <div className="fleft page-col12 fs9 lab-right tcenter">CYL</div>
              <div className="fleft page-col12 fs9 lab-right tcenter">AXIS</div>
              <div className="fleft page-col12 fs9 tcenter">V.A.</div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col16 fs12 lab-right lab-lh16 pl2" contentEditable={true} suppressContentEditableWarning={true}>R</div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R86 || ''}</div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R89 || ''}</div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>20 / </div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col12 fs10 tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>20 / </div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col16 fs12 lab-right lab-lh28 pl2" contentEditable={true} suppressContentEditableWarning={true}>L</div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L86 || ''}</div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L89 || ''}</div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>20 / </div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col12 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col12 fs10 tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>20 / </div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col40 lab-right">
                <div className="flbox">
                  <div className="fleft page-col40 fs9 lab-right tcenter">SUBJ SPHERE</div>
                  <div className="fleft page-col30 fs9 lab-right tcenter">CYL</div>
                  <div className="fleft page-col30 fs9 tcenter">AXIS</div>
                </div>
                <div className="flbox lab-bottom">
                  <div className="fleft page-col40 fs12 lab-right lab-lh28 pl2" contentEditable={true} suppressContentEditableWarning={true}>R</div>
                  <div className="fleft page-col30 fs10 lab-right tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R30 || ''}</div>
                  <div className="fleft page-col30 fs10 tcenter lab-lh16" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R31 || ''}</div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col40 fs12 lab-right lab-lh28 pl2" contentEditable={true} suppressContentEditableWarning={true}>L</div>
                  <div className="fleft page-col30 fs10 lab-right tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L30 || ''}</div>
                  <div className="fleft page-col30 fs10 tcenter lab-lh28" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L31 || ''}</div>
                </div>
              </div>
              <div className="fleft page-col15 lab-right">
                <div className="fs9 tcenter">V.A.</div>
                <div className="fs10 lab-bottom pl2 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>{examData?.R32 ? `R ${examData?.R32}` : 'R20 /'}</div>
                <div className="fs10 lab-bottom pl2 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>{examData?.L32 ? `B ${examData?.L32}` : 'B20 /'} </div>
                <div className="fs10 pl2 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>{examData?.C23 ? `L ${examData?.C23}` : 'L20 /'} </div>
              </div>
              <div className="fleft page-col12 lab-right">
                <div className="fs9 tcenter">PHORIAS</div>
                <div className="fs10 pl2 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>D </div>
                <div className="fs9 tcenter lab-lh18">LAT XO SO</div>
                <div className="fs10 pl2 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>C </div>
              </div>
              <div className="fleft page-col8 lab-right">
                <div className="fs9 tcenter">VERT</div>
                <div className="fs9 tcenter">R &nbsp; L</div>
              </div>
              <div className="fleft page-col25">
                <div className="fs9 tcenter">BASE OUT</div>
                <div className="flbox">
                  <div className="fleft page-col15 fs10 tcenter">D</div>
                  <div className="fleft page-col85 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
                <div className="flbox lab-bottom">
                  <div className="fleft page-col15 fs10 tcenter">C</div>
                  <div className="fleft page-col25 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col5 fs10 tcenter">/</div>
                  <div className="fleft page-col25 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col5 fs10 tcenter">/</div>
                  <div className="fleft page-col25 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col15 fs10 tcenter">D</div>
                  <div className="fleft page-col85 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col15 fs10 tcenter">C</div>
                  <div className="fleft page-col25 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col5 fs10 tcenter">/</div>
                  <div className="fleft page-col25 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col5 fs10 tcenter">/</div>
                  <div className="fleft page-col25 fs10 tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
              </div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col60 lab-right">
                <div className="flbox">
                  <div className="fleft page-col30 fs9 lab-right tcenter">AMPLITUDE</div>
                  <div className="fleft page-col20 fs9 lab-right tcenter">CAL ADD</div>
                  <div className="fleft page-col20 fs9 lab-right tcenter">TOL TO</div>
                  <div className="fleft page-col15 fs9 lab-right tcenter">Final Add</div>
                  <div className="fleft page-col15 fs9 tcenter">V.A.</div>
                </div>
                <div className="flbox lab-bottom">
                  <div className="fleft page-col30 fs12 lab-right lab-lh18 pl2" contentEditable={true} suppressContentEditableWarning={true}>R</div>
                  <div className="fleft page-col20 fs10 lab-right tcenter lab-lh18" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col20 fs10 lab-right pl2 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>+</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter lab-lh18" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col15 fs10 tright lab-lh18 pr1" contentEditable={true} suppressContentEditableWarning={true}>M</div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col30 fs12 lab-right lab-lh20 pl2" contentEditable={true} suppressContentEditableWarning={true}>L</div>
                  <div className="fleft page-col20 fs10 lab-right tcenter lab-lh20" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col20 fs10 lab-right pl2 lab-lh20" contentEditable={true} suppressContentEditableWarning={true}>-</div>
                  <div className="fleft page-col15 fs10 lab-right tcenter lab-lh20" contentEditable={true} suppressContentEditableWarning={true}></div>
                  <div className="fleft page-col15 fs10 tright lab-lh20 pr1" contentEditable={true} suppressContentEditableWarning={true}>M</div>
                </div>
              </div>
              <div className="fleft page-col8 lab-right">
                <div className="fs9 tcenter">ACA</div>
                <div className="fs9 tcenter pt2">/</div>
              </div>
              <div className="fleft page-col32">
                <div className="fs9 tcenter">TONOMETRY</div>
                <div className="flbox">
                  <div className="fleft page-col26 fs9 lab-right tcenter lab-lh16">TIME</div>
                  <div className="fleft page-col37 fs9 pl2">R</div>
                  <div className="fleft page-col37 fs9 pr2 tright">MM</div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col26 fs9 lab-right tright pr1 lab-lh12">AM</div>
                  <div className="fleft page-col37 fs9 pl2"> </div>
                  <div className="fleft page-col37 fs9 pr2 tright"></div>
                </div>
                <div className="flbox">
                  <div className="fleft page-col26 fs9 lab-right tright pr1 lab-lh12">PM</div>
                  <div className="fleft page-col37 fs9 pl2">L</div>
                  <div className="fleft page-col37 fs9 pr2 tright">MM</div>
                </div>
              </div>
            </div>
            <div className="flbox lab-bottom">
              <div className="fleft page-col25 lab-right">
                <div className="flbox">
                  <div className="fleft page-col25 fs10">
                    <div className="fs9 tcenter">ADVISED</div>
                    <div className="fs9 tcenter lab-lh20">TO</div>
                    <div className="fs9 tcenter">RETURN</div>
                  </div>
                  <div className="fleft page-col75 fs10">
                    <div className="fs9 tright pr1">MOS</div>
                    <div className="fs9 tright pr1 lab-lh20">&nbsp;</div>
                    <div className="fs9 tright pr1">YRS</div>
                  </div>
                </div>
              </div>
              <div className="fleft page-col30 lab-right">
                <div className="fs9 tcenter pr1">DIAGNOSIS</div>
                <div className="flbox">
                  <div className="fleft page-col25">
                    <div className="fs9 tcenter lab-lh16">MY</div>
                    <div className="fs9 tcenter lab-lh16">H</div>
                  </div>
                  <div className="fleft page-col25">
                    <div className="fs9 tcenter lab-lh16">MYA</div>
                    <div className="fs9 tcenter lab-lh16">HA</div>
                  </div>
                  <div className="fleft page-col25">
                    <div className="fs9 tcenter lab-lh16">CMA</div>
                    <div className="fs9 tcenter lab-lh16">CHA</div>
                  </div>
                  <div className="fleft page-col25">
                    <div className="fs9 tcenter lab-lh16">MXA</div>
                    <div className="fs9 tcenter lab-lh16">P</div>
                  </div>
                </div>
              </div>
              <div className="fleft page-col15 lab-right">
                <div className="flbox">
                  <div className="fleft page-col50">
                    <div className="fs9 tcenter lab-lh20">DE</div>
                    <div className="fs9 tcenter lab-lh20">CE</div>
                  </div>
                  <div className="fleft page-col50">
                    <div className="fs9 tcenter lab-lh20">DI</div>
                    <div className="fs9 tcenter lab-lh20">CI</div>
                  </div>
                </div>
              </div>
              <div className="fleft page-col30">
                <div className="fs9 tcenter">COUNSEL</div>
                <div className="fs9 tcenter lab-lh20" contentEditable={true} suppressContentEditableWarning={true}>&nbsp;</div>
                <div className="fs9 tcenter">TOP &nbsp; AN &nbsp; ADV &nbsp; Y</div>
              </div>
            </div>
            <div className="flbox">
              <div className="fleft page-col100 fs8 tright">WMVC02E<br/>Rev 02/11</div>
            </div>
            <div className="flbox pt6 mt6 pbbefore">
              <div className="fleft page-col5 fs10">AGE:</div>
              <div className="fleft page-col23 fs12 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}> </div>
              <div className="fleft page-col13 fs10 tright">VOCATION:</div>
              <div className="fleft page-col22 fs12 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}> </div>
              <div className="fleft page-col17 fs10 tright">AVOCATION:</div>
              <div className="fleft page-col20 fs12 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}> </div>
            </div>
            <div className="flbox pt2 mb2">
              <div className="fleft page-col10 fs10">FIRST Rx:</div>
              <div className="fleft page-col20 fs12 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}> </div>
              <div className="fleft page-col12 fs10 tright">LAST EXAM:</div>
              <div className="fleft page-col20 fs12 fwb tcenter" contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className="fleft page-col18 fs10 tright">ONT. DRIVER'S LIC:</div>
              <div className="fleft page-col20 fs12 tcenter" contentEditable={true} suppressContentEditableWarning={true}>Y &nbsp; N</div>
            </div>

            <div className="flbox lab-bottom lab-top">
              <div className="fleft page-col40 fs10 lab-right tcenter">OCULAR HISTORY</div>
              <div className="fleft page-col30 fs10 lab-right tcenter">PHYSICAL HEALTH</div>
              <div className="fleft page-col30 fs10 tcenter">HEADACHE</div>
            </div>
            <div className="flbox lab-bottom">
              <div className='fleft page-col40 lab-right'>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>HAVE YOU EVER HAD ANY INJURIES, DISEASES OR ABNORMALITIES OF EYE? Y N</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>ANY OPERATION ON EYE?</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>ANY LOSS OF VISION</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>CH COMPL BLUR NEAR FAR</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>PRES Rx WEAK LOST BROKEN</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>PAIN BURN ACHE TIRE STYES</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>HALOS FLOATERS</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>INJECTED LACRIM PHOTOPH</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>DIPLOPIA</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>FAMILY HISTORY CAT GLAC</div>
              </div>
              <div className='fleft page-col30 lab-right pl1'>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>GEN HEALTH OK POOR</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>LAST MED.</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>HISTORY OF ASTHMA</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>DIABETES ARTHRITIS</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>HBP HEART DIURETIS</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>NERVE, EPILEPSY</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>THYROID KIDNEY</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>ALLERGIES Y N</div>
                <div className="fs10 lab-lh18 tdu mr2" contentEditable={true} suppressContentEditableWarning={true}>MEDICATIONS</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}></div>
              </div>
              <div className='fleft page-col30 lab-right pl1'>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>Y N OCC</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>AM PM DLY WK</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>FR OCC TEM OLB</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>DULL SHARP</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>THROB SHOOT</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}>MILD AV SEVERE</div>
                <div className="fs10 lab-lh18" contentEditable={true} suppressContentEditableWarning={true}></div>
              </div>
            </div>
            <div className="flbox lab-bottom">
              <div className='fleft page-col25 lab-right'>
                <div className="flbox">
                  <div className='fleft page-col20 fs12 pt4'>C.T.</div>
                  <div className='fleft page-col80'>
                      <div className="flbox">
                        <div className='fleft page-col30 fs11'> </div>
                        <div className='fleft page-col35 fs11 tcenter lab-right'>H</div>
                        <div className='fleft page-col35 fs11 tcenter'>V</div>
                      </div>
                      <div className="flbox">
                        <div className='fleft page-col30 fs11 lab-bottom'>∞</div>
                        <div className='fleft page-col35 fs12 lab-bottom lab-right tcenter fwb' contentEditable={true} suppressContentEditableWarning={true}> </div>
                        <div className='fleft page-col35 fs12 lab-bottom tcenter fwb' contentEditable={true} suppressContentEditableWarning={true}> </div>
                      </div>
                      <div className="flbox">
                        <div className='fleft page-col30 fs11'>16</div>
                        <div className='fleft page-col35 fs12 tcenter lab-right fwb' contentEditable={true} suppressContentEditableWarning={true}> </div>
                        <div className='fleft page-col35 fs12 tcenter fwb' contentEditable={true} suppressContentEditableWarning={true}> </div>
                      </div>
                  </div>
                </div>
              </div>
              <div className='fleft page-col25 lab-right'>
                <div className="flbox">
                  <div className='fleft page-col30 fs11 pt4 tright'>STRAB</div>
                  <div className='fleft page-col70 fs12' contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
              </div>
              <div className='fleft page-col25 lab-right'>
                <div className="flbox">
                  <div className='fleft page-col40 fs11'></div>
                  <div className='fleft page-col60 fs10'>VERSIONS</div>
                </div>
                <div className="flbox pb1">
                  <div className='fleft page-col40 fs11'>
                    <div className="flbox">
                      <div className='fleft page-col50 fs10 lab-right'>OSF</div>
                      <div className='fleft page-col50 fs10 lab-bottom lab-right'></div>
                    </div>
                    <div className="flbox">
                      <div className='fleft page-col50 fs10 lab-right'>&nbsp;</div>
                      <div className='fleft page-col50 fs10 lab-right'></div>
                    </div>
                  </div>
                  <div className='fleft page-col60 fs11'>
                    <div className="flbox">
                      <div className='fleft page-col45 fs10 lab-right tright pr1'>ODF</div>
                      <div className='fleft page-col45 fs10 lab-bottom lab-right'></div>
                      <div className='fleft page-col10 fs10'></div>
                    </div>
                    <div className="flbox">
                      <div className='fleft page-col45 fs10 lab-right'>&nbsp;</div>
                      <div className='fleft page-col45 fs10 lab-right'></div>
                      <div className='fleft page-col10 fs10'></div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='fleft page-col25'>
                <div className="flbox">
                  <div className='fleft page-col30 fs11 pt1 tright'>NPC</div>
                  <div className='fleft page-col70 fs12 pt1 pl1' contentEditable={true} suppressContentEditableWarning={true}></div>
                </div>
              </div>
            </div>
            <div className="flbox lab-bottom">
              <div className='fleft page-col15 fs10 lab-lh20'>K-READINGS</div>
              <div className='fleft page-col5 fs10 tright'>OD</div>
              <div className='fleft page-col15 fs11 lab-lh20' contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className='fleft page-col5 fs10 tright lab-left'>OS</div>
              <div className='fleft page-col20 fs11 lab-lh20' contentEditable={true} suppressContentEditableWarning={true}></div>
              <div className='fleft page-col5 fs10 tright'>Col</div>
              <div className='fleft page-col15 fs11 lab-lh20' contentEditable={true} suppressContentEditableWarning={true}> </div>
              <div className='fleft page-col10 fs11 lab-lh20 tcenter'>N</div>
              <div className='fleft page-col5 fs11 lab-lh20 tcenter'>R</div>
              <div className='fleft page-col5 fs11 lab-lh20 tcenter'>G</div>
            </div>
            <div className="flbox">
              <div className="fleft page-col50">
                <div className="flbox lab-bottom">
                  <div className='fleft page-col40 lab-lh20 fs10'>EXTERNAL</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>OD</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>OS</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>GEN APPEARANCE</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh28 fs10'>LIDS & MARGINS</div>
                  <div className='fleft page-col60 lab-lh24 fs10 tcenter pr1'>
                    <img src="/assets/images/basic/graph2.png"></img>
                  </div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh18 fs10'>CONJUCTIVAE</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>LIMBUS</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>CORNEA</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>ANTERIOR CHAMBER</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>S &nbsp; M &nbsp; D</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>S &nbsp; M &nbsp; D</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>IRIS</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>BL &nbsp; BR &nbsp; GY</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>HZ &nbsp; GREEN</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>PIPIL SIZE</div>
                  <div className='fleft page-col60 lab-lh24 fs10 tcenter'>EQ</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>P REFLEX</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>DIRECT</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>NP &nbsp; S</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>NP &nbsp; S</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>CONSES</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>NP &nbsp; M</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>NP &nbsp; M</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>ACCOM</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>N &nbsp; F</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>N &nbsp; F</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh24 fs10'>FIELDS</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh24 fs10 tcenter'>&nbsp;</div>
                </div>
              </div>
              <div className="fleft page-col50">
                <div className="flbox lab-bottom">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>INTERNAL</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>OD</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>OS</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh22 fs10 pl1 lab-left'>LENS</div>
                  <div className='fleft page-col30 lab-lh22 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh22 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left pt6'>VITREOUS</div>
                  <div className='fleft page-col60 lab-lh20 fs10 tcenter'>
                    <img src="/assets/images/basic/graph3.png"></img>
                  </div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>G.APP OF FUNDUS</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>DISC TYPE</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>I &nbsp; II &nbsp; III &nbsp; IV</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>I &nbsp; II &nbsp; III &nbsp; IV</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>COLOUR</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>SIZE</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>S &nbsp; N &nbsp; L</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>S &nbsp; N &nbsp; L</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>MARGIN</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>Y &nbsp; N</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>Y &nbsp; N</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>CUPPING AREA</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>DEPTH</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>RATIO</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>LAM. CRIB</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>POSITION</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>C &nbsp; N &nbsp; T</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>C &nbsp; N &nbsp; T</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>COURSE</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>NT &nbsp; T</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>NT &nbsp; T</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>AV RATIO</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>CROSSING</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>&nbsp;</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>NV PULSE</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>Y &nbsp; N</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>Y &nbsp; N</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>FOV. REF.</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>Y &nbsp; N</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tcenter'>Y &nbsp; N</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1 lab-left'>PERIPHERY</div>
                  <div className='fleft page-col30 lab-lh20 fs10 tright pr3'>R</div>
                  <div className='fleft page-col30 lab-lh20 fs10 pl3'>L</div>
                </div>
                <div className="flbox">
                  <div className='fleft page-col40 lab-lh20 fs10 pl1'>&nbsp;</div>
                  <div className='fleft page-col60 lab-lh20 fs10 tcenter'>
                    <img src="/assets/images/basic/graph.png"></img>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Field.PrintedOn />
        </Control.Print.Data>    
      </>)}
      {showMenu && (<div ref={contextMenuRef}><Control.PrintContextMenu keyId={keyId} showMenu={showMenu} formName={formName}  setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Print />
    </Control.Print.Frame>
  );
};

export default ExamPrint2;